import cx from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';
import { debounce, throttle } from 'lodash';

import AlertDialog from 'components/AlertDialog.jsx';
import BirthdayInterruptModal from 'setup/components/BirthdayInterruptModal.jsx';
import BoxBody from 'components/BoxBody.jsx';
import Button from 'components/Button.jsx';
import Buttons from 'components/Buttons.jsx';
import ConfirmDialog from 'components/ConfirmDialog.jsx';
import CurrentUser from 'auth/CurrentUser';
import Dialog from 'components/Dialog.jsx';
import DialogContent from 'components/DialogContent.jsx';
import enumerate from 'lib/enumerate';
import ErrorActions from 'actions/ErrorActions';
import errorLogger from 'utils/errorLogger';
import MediaObject from 'components/MediaObject.jsx';
import Form from 'components/Form.jsx';
import FormInput from 'components/FormInput.jsx';
import Glyph from 'components/Glyph.jsx';
import Group from 'entities/Group';
import GroupActions from 'groups/actions/GroupActions';
import GroupAvatar from 'groups/components/GroupAvatar.jsx';
import GroupName from 'groups/components/GroupName.jsx';
import Groups from 'collections/Groups';
import GroupStore from 'groups/stores/GroupStore';
import HelpIcon from 'components/HelpIcon.jsx';
import Input from 'components/Input.jsx';
import JoinInfoModal from 'join/components/JoinInfoModal.jsx';
import KeyConstants from 'constants/KeyConstants';
import Label from 'components/Label.jsx';
import LeadActions from 'actions/LeadActions';
import Link from 'components/Link.jsx';
import LinkConstants from 'constants/LinkConstants';
import LoadingIndicator from 'components/LoadingIndicator.jsx';
import NameAndSignatureQuery from 'components/NameAndSignatureQuery.jsx';
import Organizations from 'collections/Organizations';
import PrependedTextInput from 'components/PrependedTextInput.jsx';
import ReportTeacherSelectModal from 'support/components/ReportTeacherSelectModal.jsx';
import Row from 'components/Row.jsx';
import ScrollableArea from 'components/ScrollableArea.jsx';
import StatusLabel from 'components/StatusLabel.jsx';
import Teachers from 'collections/Teachers';
import Text from 'components/Text.jsx';
import ToastActions from 'actions/ToastActions';
import UserActions from 'user/UserActions';
import { mountModal } from 'lib/modal';

const DEBOUNCE_DELAY = 300;
const ORGANIZATIONS_PAGE_SIZE = 20;

const STATUS = enumerate(['JOIN_CLASS', 'FIND_SCHOOL', 'FIND_TEACHER', 'SELECT_CLASS']);

import './styles/JoinGroupModal.scss';

// XXX too big, break apart
class JoinGroupModal extends React.Component {
  static propTypes = {
    footer: PropTypes.object,
    modal: PropTypes.bool,
    onJoin: PropTypes.func,
  };

  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  static defaultProps = {
    footer: null,
    modal: true,
  };

  constructor(props) {
    super(props);
    this._blockResultMouseOverWhileScrolling = false;

    this.state = {
      status: STATUS.JOIN_CLASS,
      joinEnabled: false,
      groupCode: '',
      errorMessage: null,
      highlight: -1,
      organizationQuery: '',
      loadingOrganizations: false,
      allOrganizationsLoaded: false,
      organizations: null,
      organization: null,
      teacherQuery: '',
      loadingTeachers: false,
      teachers: null,
      teacher: null,
      loadingGroups: false,
      groups: null,
    };
  }

  _blockResultMouseOverWhileScrolling: boolean;

  findGroupWithCode = debounce(code => {
    const showError = () => {
      if (code === this.state.groupCode) {
        ReactDOM.findDOMNode(this.refs.groupCode).blur();
        this.setState({
          errorMessage: `Uh oh! We couldn't find a class matching this code.
          Please contact your teacher for your class code.`,
        });
      }
    };

    Group.fetchByCode(code)
      .then(group => {
        if (code === this.state.groupCode) {
          if (group.canJoin()) {
            this.setState({
              joinEnabled: true,
              groupCode: group.getCode(),
            });
          } else {
            this.setState({ joinEnabled: false });
            showError();
          }
        }
      }, showError)
      .catch(errorLogger('JoinGroupModal fetchByCode failed'));
  }, DEBOUNCE_DELAY);

  handleClickTeacher = teacher => {
    if (teacher.isInvited()) {
      return;
    } else if (teacher.isInvitable()) {
      let subtext;
      if (teacher.isUser()) {
        subtext = 'Invite them to start using Remind.';
      } else if (teacher.isLead()) {
        subtext = 'Invite them to join Remind.';
      }

      const modal = (
        <AlertDialog
          title={`${teacher.getName()} doesn't have any classes on Remind.`}
          subtext={subtext}
          image={require('assets/images/book-pages.svg')}
          buttonText="Invite"
          onOkay={() => this.inviteTeacher(teacher)}
        />
      );

      mountModal(modal, this);
    }

    this.setState({ teacher }, this.handleToSelectClass);
  };

  handleFindSchoolInputChange = () => {
    this.setState(
      {
        organizationQuery: ReactDOM.findDOMNode(this.refs.findSchoolSearch.refs.input).value,
        loadingOrganizations: true,
        allOrganizationsLoaded: false,
      },
      this.loadOrganizations
    );
  };

  handleFindTeacherInputChange = () => {
    this.setState(
      {
        teacherQuery: ReactDOM.findDOMNode(this.refs.findTeacherSearch.refs.input).value,
        loadingTeachers: true,
      },
      this.loadTeachers
    );
  };

  handleInputKeyDown = event => {
    let results;
    let type;
    if (this.state.status === STATUS.FIND_SCHOOL) {
      results = this.state.organizations;
      type = 'organization';
    } else if (this.state.status === STATUS.FIND_TEACHER) {
      results = this.state.teachers;
      type = 'teacher';
    }

    const code = event.keyCode;
    switch (code) {
      case KeyConstants.ENTER: {
        const searchItemNode = ReactDOM.findDOMNode(
          this.refs[`${type}-searchItem-${this.state.highlight}`]
        );

        if (searchItemNode) {
          searchItemNode.click();
        }
        break;
      }
      case KeyConstants.UP:
      case KeyConstants.DOWN: {
        let { highlight } = this.state;
        if (code === KeyConstants.UP) {
          highlight = highlight >= 0 ? highlight - 1 : results.length - 1;
        } else {
          highlight = highlight < results.length ? highlight + 1 : 0;
        }
        this.setState({ highlight });

        if (highlight >= 0 && highlight < results.length) {
          const searchItemNode = ReactDOM.findDOMNode(this.refs[`${type}-searchItem-${highlight}`]);
          if (this.state.status === STATUS.FIND_SCHOOL) {
            this.refs.scrollAreaOrganizations.scrollIntoView(searchItemNode);
          } else if (this.state.status === STATUS.FIND_TEACHER) {
            this.refs.scrollAreaTeachers.scrollIntoView(searchItemNode);
          }

          this._blockResultMouseOverWhileScrolling = true;
        }
        break;
      }
      default:
        return;
    }
    event.preventDefault();
  };

  handleInvalidForm = () => {
    this.setState({ joinEnabled: false, errorMessage: null });
  };

  handleResultMouseOver = index => {
    if (this._blockResultMouseOverWhileScrolling) {
      this._blockResultMouseOverWhileScrolling = false;
      return;
    }

    if (this.state.highlight !== index) {
      this.setState({ highlight: index });
    }
  };

  handleScroll = throttle(() => {
    if (
      !this.state.loadingOrganizations &&
      this.isScrolledToBottom() &&
      !this.state.allOrganizationsLoaded
    ) {
      this.setState({ loadingOrganizations: true });
      this.loadMoreOrganizations()
        .then(organizations => {
          this.state.organizations.add(organizations.models);
          this.setState({
            loadingOrganizations: false,
            allOrganizationsLoaded: organizations.length < ORGANIZATIONS_PAGE_SIZE,
            organizations: this.state.organizations,
          });
        })
        .catch(errorLogger('JoinGroupModal loadMoreOrganizations failed'));
    }
  }, 100);

  handleSelectClass = () => {
    this.setState({ loadingGroups: true }, this.loadGroups);
  };

  handleToFindSchoolBackward = () => {
    this.setState({
      status: STATUS.FIND_SCHOOL,
      organization: null,
    });
  };

  handleToFindSchoolForward = () => {
    const switchToSearch = () => {
      this.setState(
        {
          status: STATUS.FIND_SCHOOL,
          highlight: -1,
          organizationQuery: '',
          loadingOrganizations: false,
          organizations: null,
          organization: null,
        },
        this.handleFindSchoolInputChange
      );
    };

    let user = CurrentUser.getUser();
    if (user.getRole() === 'student' && user.getBirthdate() === null) {
      const onComplete = () => {
        user = CurrentUser.getUser();
        if (user.isUnderage()) {
          ToastActions.showErrorToast('Search isn’t available for students under 13');
        } else if (user.getBirthdate() !== null) {
          switchToSearch();
        }
      };
      mountModal(<BirthdayInterruptModal onRequestHide={onComplete} />, this);
    } else {
      switchToSearch();
    }
  };

  handleToFindTeacherBackward = () => {
    this.setState({
      status: STATUS.FIND_TEACHER,
      teacher: null,
    });
  };

  handleToFindTeacherForward = () => {
    this.setState(
      {
        status: STATUS.FIND_TEACHER,
        highlight: -1,
        teacherQuery: '',
        loadingTeachers: false,
        teachers: null,
        teacher: null,
      },
      this.handleFindTeacherInputChange
    );
  };

  handleToJoinClassBackward = () => {
    this.setState({ status: STATUS.JOIN_CLASS, groupCode: '', errorMessage: null });
  };

  handleToSelectClass = () => {
    this.setState(
      {
        status: STATUS.SELECT_CLASS,
        loadingGroups: false,
        groups: null,
      },
      this.handleSelectClass
    );
  };

  handleValidForm = () => {
    this.setState({ joinEnabled: false, errorMessage: null });
    if (this.isAlreadySubscribed(this.state.groupCode)) {
      this.setState({
        errorMessage: 'You’ve already joined this class!',
      });
      return;
    }
    const group = GroupStore.getGroupForCode(this.state.groupCode);
    if (group && group.isOwnedByUser(CurrentUser.getUserId())) {
      this.setState({
        errorMessage:
          'You currently own this class. ' +
          'To receive copies of your messages, change your preferences in Notifications.',
      });
      return;
    }
    if (!this.state.groupCode) {
      return;
    }
    this.findGroupWithCode(this.state.groupCode);
  };

  inviteTeacher = teacher => {
    const onInvited = () => {
      teacher.setState('invited');
      this.forceUpdate(); // better than what was here
    };
    if (teacher.isUser()) {
      UserActions.requestForPublicGroups(teacher.id)
        .then(onInvited)
        .catch(errorLogger('JoinGroupModal requestForPublicGroups failed'));
    } else if (teacher.isLead()) {
      LeadActions.sendInvitations([teacher.id])
        .then(onInvited)
        .catch(errorLogger('JoinGroupModal sendInvitations failed'));
    }
  };

  isAlreadySubscribed = code => {
    const group = GroupStore.getGroupForCode(code);
    return group && !group.isOwnedByUser(CurrentUser.getUserId());
  };

  isScrolledToBottom = () => {
    return this.refs.scrollAreaOrganizations.isScrolledToBottom(100);
  };

  joinGroup = data => {
    const { groupCode } = data;
    GroupActions.subscribeToGroupWithCode(groupCode).then(
      group => {
        GroupActions.addToGroupsJoinedDuringSetup(group);
        ToastActions.showSuccessToast(`You’ve joined ${group.getName()}`);
        this.props.onRequestHide();
        this.props.onJoin(group);
      },
      error => {
        if (error.code === 'information_required') {
          // TODO model this via Flux so this component doesn't manage the route
          this.context.router.push(`/information_required/${groupCode}`);
        } else {
          ErrorActions.serverError(error);
          console.warn('Join group modal failed to subscribe', error);
        }
      }
    );
  };

  loadGroups = (): void => {
    Groups.find(this.state.teacher.id)
      .then(groups => {
        this.setState({
          loadingGroups: false,
          groups,
        });
      })
      .catch(() => {
        const modal = (
          <AlertDialog
            glyph="caution"
            glyphColor="lava"
            title="Sorry, you can't view any more teachers right now."
            onRequestHide={this.handleToFindTeacherBackward}
          />
        );
        mountModal(modal, this);
      });
  };

  loadMoreOrganizations = () => {
    const query = this.state.organizationQuery;
    const numOrganizations = this.state.organizations.length;
    const page = Math.floor(numOrganizations / ORGANIZATIONS_PAGE_SIZE) + 1;
    return Organizations.search('School', query, page, ORGANIZATIONS_PAGE_SIZE);
  };

  loadOrganizations = debounce(() => {
    const query = this.state.organizationQuery;
    Organizations.search('School', query)
      .then(organizations => {
        // Don't update if the query changed
        if (query !== this.state.organizationQuery) {
          return;
        }
        this.setState({
          loadingOrganizations: false,
          organizations,
        });
      })
      .catch(errorLogger('JoinGroupModal search failed'));
  }, DEBOUNCE_DELAY);

  loadTeachers = debounce(() => {
    const query = this.state.teacherQuery;
    Teachers.getTeachersForOrganizationId(this.state.organization.id, query)
      .then(teachers => {
        // Don't update if the query changed
        if (query !== this.state.teacherQuery) {
          return;
        }
        this.setState({
          loadingTeachers: false,
          teachers,
        });
      })
      .catch(() => {
        const modal = (
          <AlertDialog
            glyph="caution"
            glyphColor="lava"
            title="Sorry, you can't view any more schools right now."
            onRequestHide={this.handleToFindSchoolBackward}>
            <Link
              href={LinkConstants.SELECTING_SCHOOL}
              block
              target="_blank"
              className="JoinGroupModal-learnMore"
              eventElementName="JoinGroupModal-learnMore">
              Learn more
            </Link>
          </AlertDialog>
        );
        mountModal(modal, this);
      });
  }, DEBOUNCE_DELAY);

  reportTeacher = () => {
    const modal = <ReportTeacherSelectModal teacher={this.state.teacher} />;
    mountModal(modal, this);
  };

  subscribeToGroup = group => {
    const markAsSubscribed = () => {
      ToastActions.showSuccessToast(`You’ve joined ${group.getName()}`);
      group.setState('subscribed');
      this.forceUpdate(); // better than what was here
    };
    GroupActions.subscribeToGroup(group).then(markAsSubscribed, error => {
      if (error.code === 'information_required') {
        const modal = <JoinInfoModal code={group.getCodeOrError()} onDone={markAsSubscribed} />;
        mountModal(modal, this);
        return;
      }
      ToastActions.showDefaultErrorToast();
      console.warn('Join group modal failed to subscribe', error);
    });
  };

  unsubscribeFromGroup = group => {
    const onLeave = () => {
      GroupActions.unsubscribeFromGroup(group)
        .then(() => {
          group.setState('available');
          this.forceUpdate(); // better than what was here
          ToastActions.showSuccessToast(`You’ve left ${group.getName()}`);
        })
        .catch(errorLogger('JoinGroupModal unsubscribeFromGroup failed'));
    };
    const modal = (
      <ConfirmDialog
        title="Leave this class?"
        subtext={`Once you leave, you will no longer receive messages from ${group.getName()}`}
        actionButtonText="Leave"
        cancelButtonText="Cancel"
        onConfirm={onLeave}
      />
    );
    mountModal(modal, this);
  };

  renderFindSchool = () => {
    const Wrapper = this.props.modal ? Dialog : DialogContent;

    return (
      <Wrapper
        key="find-school"
        {...this.props}
        size="small"
        className="JoinGroupModal"
        title="Select your school"
        subtext="First, find the school you attend."
        hidePreviousButton={false}
        onRequestPrevious={this.handleToJoinClassBackward}>
        <BoxBody divided full>
          <div className="JoinGroupModal-inputWrap">
            <Input
              autofocus
              glyph="search"
              key="findSchoolSearch"
              noBorder
              placeholder="Search for a school"
              ref="findSchoolSearch"
              size="large"
              type="text"
              value={this.state.organizationQuery}
              onChange={this.handleFindSchoolInputChange}
              onKeyDown={this.handleInputKeyDown}
            />
          </div>
          <ScrollableArea
            className="JoinGroupModal-list"
            ref="scrollAreaOrganizations"
            onScroll={this.handleScroll}
            useBodyChildrenHeight>
            {this.renderOrganizations()}
            {this.state.loadingOrganizations && this.renderLoadingIndicator()}
          </ScrollableArea>
        </BoxBody>
      </Wrapper>
    );
  };

  renderFindTeacher = () => {
    const Wrapper = this.props.modal ? Dialog : DialogContent;

    return (
      <Wrapper
        key="find-teacher"
        {...this.props}
        size="small"
        className="JoinGroupModal"
        title="Find your teacher"
        subtext={this.state.organization.getName()}
        hidePreviousButton={false}
        onRequestPrevious={this.handleToFindSchoolBackward}>
        <BoxBody divided full>
          <div className="JoinGroupModal-inputWrap">
            <Input
              autofocus
              glyph="search"
              noBorder
              placeholder="Search for a teacher"
              ref="findTeacherSearch"
              size="large"
              type="text"
              value={this.state.teacherQuery}
              onChange={this.handleFindTeacherInputChange}
              onKeyDown={this.handleInputKeyDown}
            />
          </div>
          <ScrollableArea
            className="JoinGroupModal-list"
            ref="scrollAreaTeachers"
            useBodyChildrenHeight>
            {this.renderTeachers()}
            {this.state.loadingTeachers && this.renderLoadingIndicator()}
          </ScrollableArea>
        </BoxBody>
      </Wrapper>
    );
  };

  renderGroup = group => {
    let join;

    if (group.isAvailable()) {
      join = (
        <Button
          eventElementName="JoinGroupModal-join"
          level="secondary"
          size="xsmall"
          weight="semibold"
          onClick={() => this.subscribeToGroup(group)}>
          Join
        </Button>
      );
    } else if (group.isSubscribed()) {
      const enabled = group.canLeave();

      join = (
        <Button
          eventElementName="JoinGroupModal-joined"
          size="xsmall"
          weight="normal"
          disabled={!enabled}
          onClick={() => enabled && this.unsubscribeFromGroup(group)}
          aria-label="Leave class">
          <Glyph name="check" />
          Joined
        </Button>
      );
    }

    return (
      <div
        key={group.id}
        className={cx('JoinGroupModal-list-item', {
          'JoinGroupModal-list-item--hasAction': group.isAvailable() || group.isSubscribed(),
        })}>
        <MediaObject center figure="right" className="JoinGroupModal-list-item-body">
          <MediaObject center figure="left" gutter="xsmall">
            <GroupAvatar group={group} className="JoinGroupModal-list-item-avatar" />
            <Text size="xxsmall">
              <Text element="h3" truncate>
                <GroupName group={group} />
              </Text>
              <Text color="eclipse">@{group.getCodeOrError()}</Text>
            </Text>
          </MediaObject>
          {join}
        </MediaObject>
      </div>
    );
  };

  renderGroups = () => {
    if (!this.state.groups) {
      return null;
    }
    return this.state.groups.map(this.renderGroup);
  };

  renderJoinClass = () => {
    // TODO: Manually implemented MediaObject
    // (waiting for fix: https://github.com/christianalfoni/formsy-react/issues/151)
    const Wrapper = this.props.modal ? Dialog : DialogContent;
    const user = CurrentUser.getUser();

    const label = (
      <Label htmlFor="groupCode">
        Enter class code
        <HelpIcon
          tooltipText={`
            This is the unique code you can use to join and receive messages from your class.
          `}
        />
      </Label>
    );

    const button = (
      <Button
        type="submit"
        id="JoinGroupModal-joinButton"
        size="small"
        disabled={!this.state.joinEnabled}
        data-test-selector="join-group-modal-submit">
        Join
      </Button>
    );

    return (
      <Wrapper
        key="join-class"
        {...this.props}
        className="JoinGroupModal"
        title="Join a class"
        size="small">
        <BoxBody divided>
          <Form
            onSubmit={this.joinGroup}
            onValid={this.handleValidForm}
            onInvalid={this.handleInvalidForm}>
            <FormInput
              label={label}
              button={button}
              component={PrependedTextInput}
              errorMessage={this.state.errorMessage}
              prepend="@"
              autofocus
              ref="groupCode"
              name="groupCode"
              value={this.state.groupCode}
              onChange={event =>
                this.setState({
                  groupCode: event.target.value,
                })
              }
              validations={{
                minLength: 3,
                maxLength: 10,
              }}
              validationErrors={{
                minLength: 'The code must be at least 3 characters long.',
                maxLength: 'The code cannot be over 10 characters long.',
              }}
            />
          </Form>
          {user.getCountryCode() === 'US' &&
            !user.isUnderage() && (
              <Button
                className="JoinGroupModal-search"
                eventElementName="JoinGroupModal-search"
                level="text"
                weight="semibold"
                color="brand"
                onClick={this.handleToFindSchoolForward}>
                <Glyph name="search" />
                <span>Search for your class instead</span>
              </Button>
            )}
        </BoxBody>
        {this.props.footer}
      </Wrapper>
    );
  };

  renderLoadingIndicator = () => {
    return <LoadingIndicator height={100} size="small" />;
  };

  renderNotFound = message => {
    return (
      <div className="JoinGroupModal-list-item--notFound JoinGroupModal-list-item">
        <div className="JoinGroupModal-list-item--notFound-body JoinGroupModal-list-item-body">
          <Text align="center" element="div" className="JoinGroupModal-list-item--notFound-inner">
            <Text element="h3" size="small" weight="semibold">
              {message}
            </Text>
            <Text color="thunder" size="xxsmall">
              Please try again or join using the class code from your teacher.
            </Text>
          </Text>
        </div>
      </div>
    );
  };

  renderOrganization = (organization, index) => {
    return (
      <div
        className={cx('JoinGroupModal-list-item', {
          'JoinGroupModal-list-item--highlight': this.state.highlight === index,
        })}
        ref={`organization-searchItem-${index}`}
        onClick={() => this.setState({ organization }, this.handleToFindTeacherForward)}
        onMouseOver={() => this.handleResultMouseOver(index)}
        key={organization.id}>
        <div className="JoinGroupModal-list-item-body">
          <Text size="xxsmall" className="JoinGroupModal-list-item-text">
            <Text truncate>{organization.getName()}</Text>
            <Text color="eclipse">{organization.getFormattedAddress()}</Text>
          </Text>
        </div>
      </div>
    );
  };

  renderOrganizations = () => {
    if (!this.state.organizations) {
      return null;
    }
    if (this.state.organizations.length === 0 && !this.state.loadingOrganizations) {
      if (this.state.organizationQuery.length === 0) {
        return null;
      }
      return this.renderNotFound('School not found');
    }
    return this.state.organizations.map(this.renderOrganization);
  };

  renderSelectClass = () => {
    const Wrapper = this.props.modal ? Dialog : DialogContent;
    return (
      <Wrapper
        key="select-class"
        {...this.props}
        size="small"
        className="JoinGroupModal"
        title="Select a class"
        subtext={`Select one or more of ${this.state.teacher.getName()}'s classes below.`}
        hidePreviousButton={false}
        onRequestPrevious={this.handleToFindTeacherBackward}>
        <BoxBody divided>
          <Row>
            <ScrollableArea
              className="JoinGroupModal-list JoinGroupModal-list--full"
              ref="scrollAreaGroups"
              showShadows
              useBodyChildrenHeight>
              {this.renderGroups()}
              {this.state.loadingGroups && this.renderLoadingIndicator()}
            </ScrollableArea>
          </Row>
          <Buttons axis="y">
            <Button
              className="JoinGroupModal-done"
              eventElementName="JoinGroupModal-done"
              onClick={this.props.onRequestHide}>
              Done
            </Button>
            <Link color="thunder" onClick={this.reportTeacher}>
              <Glyph name="flag" size="medium" />
              Report this person
            </Link>
          </Buttons>
        </BoxBody>
      </Wrapper>
    );
  };

  renderTeacher = (teacher, index) => {
    let invite;

    if (teacher.isInvitable()) {
      invite = (
        <Button
          level="secondary"
          size="xsmall"
          weight="normal"
          onClick={event => {
            event.stopPropagation();
            this.inviteTeacher(teacher);
          }}>
          Invite
        </Button>
      );
    } else if (teacher.isInvited()) {
      invite = <StatusLabel>Invited</StatusLabel>;
    }

    return (
      <div
        className={cx({
          'JoinGroupModal-list-item': true,
          'JoinGroupModal-list-item--hasAction': teacher.isInvitable(),
          'JoinGroupModal-list-item--highlight': this.state.highlight === index,
          'JoinGroupModal-list-item--disabled': teacher.isInvited(),
        })}
        ref={`teacher-searchItem-${index}`}
        onClick={() => this.handleClickTeacher(teacher)}
        onMouseOver={() => this.handleResultMouseOver(index)}
        key={teacher.id}>
        <div className="JoinGroupModal-list-item-body">
          <MediaObject figure="right" center>
            <Text size="xxsmall">
              <Text element="h3" truncate>
                <NameAndSignatureQuery
                  displayName={teacher.getName()}
                  fullName={teacher.getFullName()}
                  query={this.state.teacherQuery}
                />
              </Text>
              <Text color="eclipse" truncate>
                {teacher.getPreview()}
              </Text>
            </Text>
            {invite}
          </MediaObject>
        </div>
      </div>
    );
  };

  renderTeachers = () => {
    if (!this.state.teachers) {
      return null;
    }
    if (this.state.teachers.length === 0 && !this.state.loadingTeachers) {
      return this.renderNotFound('Teacher not found');
    }
    return this.state.teachers.map(this.renderTeacher);
  };

  render() {
    // XXX antipattern. these should each be separate components
    const body = {
      [STATUS.JOIN_CLASS]: this.renderJoinClass,
      [STATUS.FIND_SCHOOL]: this.renderFindSchool,
      [STATUS.FIND_TEACHER]: this.renderFindTeacher,
      [STATUS.SELECT_CLASS]: this.renderSelectClass,
    }[this.state.status]();
    return body;
  }
}

export default JoinGroupModal;
